import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, X, FloppyDisk, Lightning } from "@phosphor-icons/react";

const SEV = {
    low: { bg: "bg-slate-500/20", text: "text-slate-200", border: "border-slate-500/40" },
    medium: { bg: "bg-amber-500/20", text: "text-amber-200", border: "border-amber-500/40" },
    high: { bg: "bg-orange-500/20", text: "text-orange-200", border: "border-orange-500/40" },
    critical: { bg: "bg-red-500/20", text: "text-red-200", border: "border-red-500/40" },
};

export default function EquipmentFaultsPanel({ equipmentId, canEdit }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [search, setSearch] = useState("");

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get(`/equipment/${equipmentId}/faults`);
            setItems(res.data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [equipmentId]);

    useEffect(() => { load(); }, [load]);

    const filtered = items.filter((f) => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return f.code.toLowerCase().includes(q) || f.title.toLowerCase().includes(q) || f.symptoms.toLowerCase().includes(q);
    });

    const remove = async (fault) => {
        if (!window.confirm(`Delete fault ${fault.code}?`)) return;
        try {
            await api.delete(`/equipment/faults/${fault.id}`);
            toast.success("Fault removed");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="p-4 sm:p-8" data-testid="equipment-faults-panel">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                    <div className="text-cyan-300 font-display font-bold tracking-[0.18em] text-sm uppercase flex items-center gap-2"><Lightning size={14} weight="bold" /> Fault Library</div>
                    <div className="text-xs text-slate-400">Curated faults used as grounding context by the AI Fault Finder.</div>
                </div>
                <div className="flex items-center gap-2">
                    <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search code / title…" className="bg-white/10 text-white placeholder-slate-400 text-sm rounded-sm px-2.5 py-1.5 outline-none border border-cyan-400/20 focus:border-cyan-400" data-testid="fault-search" />
                    {canEdit && (
                        <button onClick={() => setEditing("new")} className="bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-bold uppercase tracking-wide text-xs px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5" data-testid="add-fault-btn">
                            <Plus size={13} weight="bold" /> Add fault
                        </button>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="text-slate-400 py-6 text-center">Loading…</div>
            ) : filtered.length === 0 ? (
                <div className="text-slate-400 py-10 text-center text-sm border border-dashed border-cyan-400/20 rounded-md">
                    {items.length === 0 ? "No faults defined yet. Add your equipment fault codes here so the AI Fault Finder can use them as grounding context." : "No faults match your search."}
                </div>
            ) : (
                <div className="space-y-2">
                    {filtered.map((f) => {
                        const sev = SEV[f.severity] || SEV.medium;
                        return (
                            <div key={f.id} className={`rounded-md p-3 border ${sev.border} ${sev.bg}`} data-testid={`fault-row-${f.code}`}>
                                <div className="flex items-start justify-between gap-2 flex-wrap">
                                    <div className="flex items-baseline gap-2 flex-1 min-w-0">
                                        <div className={`font-display font-black tabular ${sev.text}`}>{f.code}</div>
                                        <div className="font-bold text-white flex-1 min-w-0">{f.title}</div>
                                        <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm ${sev.bg} ${sev.text} border ${sev.border}`}>{f.severity}</span>
                                    </div>
                                    {canEdit && (
                                        <div className="flex items-center gap-1">
                                            <button onClick={() => setEditing(f)} className="p-1.5 rounded bg-white/5 hover:bg-white/15 text-slate-200" data-testid={`fault-edit-${f.code}`}><PencilSimple size={12} /></button>
                                            <button onClick={() => remove(f)} className="p-1.5 rounded bg-red-500/20 hover:bg-red-500/30 text-red-300" data-testid={`fault-delete-${f.code}`}><Trash size={12} /></button>
                                        </div>
                                    )}
                                </div>
                                <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                                    <FieldRow label="Symptoms" value={f.symptoms} />
                                    <FieldRow label="Likely cause" value={f.likely_cause} />
                                    <FieldRow label="Resolution" value={f.resolution_steps} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {editing && (
                <FaultFormModal
                    equipmentId={equipmentId}
                    fault={editing === "new" ? null : editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}
        </div>
    );
}

function FieldRow({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-0.5">{label}</div>
            <div className="text-slate-100 whitespace-pre-wrap leading-snug">{value}</div>
        </div>
    );
}

function FaultFormModal({ equipmentId, fault, onClose, onSaved }) {
    const [f, setF] = useState({
        code: fault?.code || "",
        title: fault?.title || "",
        symptoms: fault?.symptoms || "",
        likely_cause: fault?.likely_cause || "",
        resolution_steps: fault?.resolution_steps || "",
        severity: fault?.severity || "medium",
        tags: (fault?.tags || []).join(", "),
    });
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (!f.code.trim() || !f.title.trim() || !f.symptoms.trim() || !f.likely_cause.trim() || !f.resolution_steps.trim()) {
            toast.error("All fields except tags are required");
            return;
        }
        const payload = {
            code: f.code.trim(),
            title: f.title.trim(),
            symptoms: f.symptoms.trim(),
            likely_cause: f.likely_cause.trim(),
            resolution_steps: f.resolution_steps.trim(),
            severity: f.severity,
            tags: f.tags.split(",").map((s) => s.trim()).filter(Boolean),
        };
        try {
            setSaving(true);
            if (fault) {
                await api.patch(`/equipment/faults/${fault.id}`, payload);
                toast.success("Fault updated");
            } else {
                await api.post(`/equipment/${equipmentId}/faults`, payload);
                toast.success("Fault created");
            }
            onSaved && onSaved();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[95] bg-slate-900/85 flex items-start sm:items-center justify-center p-2 overflow-y-auto" data-testid="fault-form-modal">
            <div className="bg-white rounded-lg w-full max-w-xl shadow-xl border border-slate-200 relative">
                <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center" data-testid="fault-form-close"><X size={16} /></button>
                <div className="px-5 py-4 border-b border-slate-200">
                    <div className="uppercase-label text-[10px] text-[var(--grindrod-red)]">{fault ? "Edit Fault" : "New Fault"}</div>
                    <h3 className="font-display font-black text-xl text-[var(--grindrod-navy)]">{fault ? "Edit fault definition" : "Add fault definition"}</h3>
                </div>
                <div className="p-5 space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                        <Field label="Code">
                            <input type="text" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} className="g-input tabular uppercase" data-testid="fault-form-code" placeholder="E12" />
                        </Field>
                        <Field label="Severity">
                            <select value={f.severity} onChange={(e) => setF({ ...f, severity: e.target.value })} className="g-input" data-testid="fault-form-severity">
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                                <option value="critical">Critical</option>
                            </select>
                        </Field>
                        <div className="col-span-1" />
                    </div>
                    <Field label="Title">
                        <input type="text" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="g-input" data-testid="fault-form-title" placeholder="VSD Overcurrent Trip" />
                    </Field>
                    <Field label="Symptoms">
                        <textarea rows={2} value={f.symptoms} onChange={(e) => setF({ ...f, symptoms: e.target.value })} className="g-input" data-testid="fault-form-symptoms" placeholder="What does the operator see?" />
                    </Field>
                    <Field label="Likely cause">
                        <textarea rows={2} value={f.likely_cause} onChange={(e) => setF({ ...f, likely_cause: e.target.value })} className="g-input" data-testid="fault-form-cause" />
                    </Field>
                    <Field label="Resolution steps">
                        <textarea rows={4} value={f.resolution_steps} onChange={(e) => setF({ ...f, resolution_steps: e.target.value })} className="g-input" data-testid="fault-form-steps" placeholder="1. Isolate&#10;2. Inspect…" />
                    </Field>
                    <Field label="Tags (comma-separated)">
                        <input type="text" value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} className="g-input" data-testid="fault-form-tags" placeholder="VSD, trip" />
                    </Field>
                </div>
                <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
                    <button onClick={onClose} className="text-sm px-3 py-2 rounded-sm bg-slate-100 hover:bg-slate-200">Cancel</button>
                    <button onClick={submit} disabled={saving} className="text-sm px-3 py-2 rounded-sm bg-[var(--grindrod-navy)] text-white inline-flex items-center gap-2 disabled:opacity-60" data-testid="fault-form-save">
                        <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-slate-500 font-bold block mb-1">{label}</span>
            {children}
        </label>
    );
}
